import { z } from 'zod';

/**
 * Resource-allocation contracts (M5 — PM-2032..2038, ADR 0021/0023).
 *
 * One aggregate for both NAMED (a concrete resource) and GENERIC (a
 * competency-only slot) allocations. Statuses are declared extensible
 * (ADR 0023) — open strings validated by Zod, never closed enums. The three
 * known values ship as constants.
 */

export const ALLOCATION_STATUSES = ['NOT_STARTED', 'IN_PROGRESS', 'COMPLETED'] as const;
export type AllocationStatus = (typeof ALLOCATION_STATUSES)[number];

/** Extensible per ADR 0023 — validates the shape, not a closed set. */
export const AllocationStatusSchema = z.string().min(1);

export const RESOURCE_KINDS = ['NAMED', 'GENERIC'] as const;
export type AllocationResourceKind = (typeof RESOURCE_KINDS)[number];
export const AllocationResourceKindSchema = z.enum(RESOURCE_KINDS);

/** DL-023 semantics shared with work items: COMPLETED (and future terminal states) is inactive. */
export const ALLOCATION_ACTIVE_STATUSES = ['NOT_STARTED', 'IN_PROGRESS'] as const;
export const isActiveAllocationStatus = (status: string): boolean =>
  (ALLOCATION_ACTIVE_STATUSES as readonly string[]).includes(status);

const allocatedBudget = z.number().min(0).max(1_000_000).multipleOf(0.01);

/**
 * Create an allocation (PM-2033). Zero-hour allocations are legal (a
 * placeholder that reserves the seat, not the hours). NAMED may carry an
 * optional competency context roleId (DL-007); GENERIC requires one.
 */
export const CreateAllocationRequestSchema = z.discriminatedUnion('resourceKind', [
  z.object({
    resourceKind: z.literal('NAMED'),
    workItemId: z.string().uuid(),
    resourceId: z.string().uuid(),
    roleId: z.string().uuid().optional(),
    allocatedBudget,
    status: AllocationStatusSchema.optional(),
  }),
  z.object({
    resourceKind: z.literal('GENERIC'),
    workItemId: z.string().uuid(),
    roleId: z.string().uuid(),
    allocatedBudget,
    status: AllocationStatusSchema.optional(),
  }),
]);
export type CreateAllocationRequest = z.infer<typeof CreateAllocationRequestSchema>;

export const UpdateAllocationBudgetRequestSchema = z.object({
  expectedVersion: z.number().int().min(1),
  allocatedBudget,
});
export type UpdateAllocationBudgetRequest = z.infer<typeof UpdateAllocationBudgetRequestSchema>;

export const UpdateAllocationStatusRequestSchema = z.object({
  expectedVersion: z.number().int().min(1),
  status: AllocationStatusSchema,
});
export type UpdateAllocationStatusRequest = z.infer<typeof UpdateAllocationStatusRequestSchema>;

const isoDate = z.string().regex(/^\d{4}-\d{2}-\d{2}$/, 'Expected an ISO date (YYYY-MM-DD)');

/** The allocation frame (PM-2035, DL-030). `null` clears a boundary. */
export const UpdateAllocationFrameRequestSchema = z.object({
  expectedVersion: z.number().int().min(1),
  startDate: isoDate.nullable(),
  targetDate: isoDate.nullable(),
});
export type UpdateAllocationFrameRequest = z.infer<typeof UpdateAllocationFrameRequestSchema>;

/** Resolve part (or all) of a GENERIC slot into a NAMED allocation (PM-2037). */
export const ResolveGenericRequestSchema = z.object({
  resourceId: z.string().uuid(),
  hoursToResolve: z.number().gt(0).max(1_000_000).multipleOf(0.01),
  /** Overrides the competency context; defaults to the generic slot's roleId (DL-007). */
  roleId: z.string().uuid().optional(),
});
export type ResolveGenericRequest = z.infer<typeof ResolveGenericRequestSchema>;

export const AllocationRollupStateSchema = z.enum(['DIRECT', 'ROLLED_UP']);
export type AllocationRollupState = z.infer<typeof AllocationRollupStateSchema>;

export const AllocationResponseSchema = z.object({
  id: z.string().uuid(),
  workItemId: z.string().uuid(),
  resourceKind: AllocationResourceKindSchema,
  resourceId: z.string().uuid().nullable(),
  roleId: z.string().uuid().nullable(),
  resolvedFromId: z.string().uuid().nullable(),
  allocatedBudget: z.number(),
  startDate: z.string().nullable(),
  targetDate: z.string().nullable(),
  rollupState: AllocationRollupStateSchema,
  originalAllocatedBudget: z.number().nullable(),
  status: AllocationStatusSchema,
  /** Maintained running balance (DL-022) — never recomputed from bookings. */
  deviation: z.number(),
  retiredAt: z.string().nullable(),
  version: z.number().int(),
  createdAt: z.string(),
  updatedAt: z.string(),
  /** Query-time roll-up (PM-2036, ADR 0022) — present on tree reads. */
  childAllocationSum: z.number().optional(),
  /** DIRECT → allocatedBudget; ROLLED_UP → childAllocationSum. */
  operativeAllocation: z.number().optional(),
});
export type AllocationResponse = z.infer<typeof AllocationResponseSchema>;

export const AllocationListResponseSchema = z.object({
  allocations: z.array(AllocationResponseSchema),
});
export type AllocationListResponse = z.infer<typeof AllocationListResponseSchema>;

export const AllocationIdParamsSchema = z.object({ id: z.string().uuid() });
export type AllocationIdParams = z.infer<typeof AllocationIdParamsSchema>;

export const AllocationTreeQuerySchema = z.object({
  /** Retired GENERIC slots are audit rows — excluded from active planning views by default. */
  includeRetired: z.coerce.boolean().optional().default(false),
});
export type AllocationTreeQuery = z.infer<typeof AllocationTreeQuerySchema>;
